import { useState } from 'react';
import { comments, hotTopics } from '@/mocks/home';
import SectionHeading from '@/components/base/SectionHeading';

const tagStyle: Record<string, string> = {
  沸: 'bg-primary-500 text-background-50',
  热: 'bg-accent-500 text-accent-950',
  议: 'bg-secondary-200 text-secondary-900',
};

const avatarStyle = ['bg-primary-100 text-primary-700', 'bg-accent-100 text-accent-900', 'bg-secondary-200 text-secondary-900'];

export default function HotDiscussion() {
  const [liked, setLiked] = useState<Record<number, boolean>>({});
  const [voted, setVoted] = useState<number | null>(null);

  const toggleLike = (id: number) => {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section id="discussion" className="border-b border-background-200/70 bg-secondary-50">
      <div className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-6 md:py-16 lg:px-10">
        <SectionHeading
          kicker="互动区"
          title="评论区比存档还热闹"
          desc="每条存档下面都有人补充细节、提出质疑。热度最高的讨论会留在这一页，看看大家怎么说。"
          accent="secondary"
          action={{ label: '进入讨论', href: '#discussion' }}
        />

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-fire-line text-primary-600"></i>
                  热议话题榜
                </h3>
                <span className="text-xs text-foreground-500">实时更新</span>
              </div>
              <ul className="flex flex-col gap-3">
                {hotTopics.map((topic) => (
                  <li key={topic.id}>
                    <button
                      type="button"
                      onClick={() => setVoted(topic.id)}
                      className={`w-full cursor-pointer rounded-md border p-3 text-left transition-colors ${
                        voted === topic.id
                          ? 'border-primary-300 bg-primary-50'
                          : 'border-background-200 bg-background-50 hover:border-secondary-300 hover:bg-secondary-50'
                      }`}
                    >
                      <div className="flex items-start gap-2.5">
                        <span
                          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-xs ${
                            tagStyle[topic.tag] ?? 'bg-secondary-200 text-secondary-900'
                          }`}
                        >
                          {topic.tag}
                        </span>
                        <span className="flex-1 text-sm leading-snug text-foreground-800">
                          {topic.title}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center gap-4 pl-7 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-fire-line text-accent-600"></i>
                          {topic.heat} 热度
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-chat-3-line"></i>
                          {topic.replies} 回复
                        </span>
                        {voted === topic.id ? (
                          <span className="flex items-center gap-1 text-primary-600">
                            <i className="ri-checkbox-circle-line"></i>
                            已投票
                          </span>
                        ) : (
                          <span className="text-foreground-400">点击投票</span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-chat-smile-2-line text-secondary-600"></i>
                  最新评论
                </h3>
                <span className="rounded bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                  {comments.length} 条新留言
                </span>
              </div>

              <ul className="flex flex-col gap-4">
                {comments.map((comment, index) => (
                  <li
                    key={comment.id}
                    className="flex gap-3 border-b border-background-200 pb-4 last:border-0 last:pb-0"
                  >
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${avatarStyle[index % avatarStyle.length]}`}
                    >
                      {comment.initial}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-sm font-medium text-foreground-900">
                          {comment.name}
                        </span>
                        <span className="rounded bg-accent-100 px-1.5 py-0.5 text-xs text-accent-900">
                          {comment.role}
                        </span>
                        <span className="text-xs text-foreground-400">{comment.time}</span>
                      </div>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">
                        {comment.content}
                      </p>
                      <div className="mt-2 flex items-center gap-4 text-xs">
                        <button
                          type="button"
                          onClick={() => toggleLike(comment.id)}
                          className={`flex cursor-pointer items-center gap-1 transition-colors ${
                            liked[comment.id]
                              ? 'text-primary-600'
                              : 'text-foreground-500 hover:text-primary-600'
                          }`}
                        >
                          <i className={liked[comment.id] ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                          {comment.likes + (liked[comment.id] ? 1 : 0)}
                        </button>
                        <span className="flex cursor-pointer items-center gap-1 text-foreground-500 transition-colors hover:text-secondary-700">
                          <i className="ri-chat-3-line"></i>
                          回复
                        </span>
                        <span className="flex cursor-pointer items-center gap-1 text-foreground-500 transition-colors hover:text-secondary-700">
                          <i className="ri-share-forward-line"></i>
                          分享
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-5 flex flex-col gap-2 rounded-md border border-background-200 bg-background-100 p-3 sm:flex-row sm:items-center">
                <input
                  placeholder="说说你对这条黑料存档的看法…"
                  className="flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
                <button
                  type="button"
                  className="shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-secondary-600"
                >
                  发表评论
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}